import * as React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgCheckCircleBroken } from "@digits-shared/components/SVGIcons/line/CheckCircleBroken.svg"
import { SvgXCircle } from "@digits-shared/components/SVGIcons/line/XCircle.svg"
import { ACCESSIBLY_HIDDEN } from "@digits-shared/components/UI/Elements/Form/InputStyles"
import { Input } from "@digits-shared/DesignSystem/FormElements/Input"
import objectHelper from "@digits-shared/helpers/objectHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import { InputStyled as DeprecatedInput } from "./Input"
import { Label } from "./Label"

const MIN_LENGTH = 10
const LOWERCASE_REGEX = /[a-z]/
const UPPERCASE_REGEX = /[A-Z]/
const NUMERAL_REGEX = /[0-9]/
const SPECIAL_REGEX = /[^A-Za-z0-9]/

/*
  STYLES
*/

export const PasswordBlock = styled.div`
  position: relative;
  width: 100%;
  text-align: left;
`

const PasswordLabel = styled(Label)<{ status: boolean | RequirementStatus; $hideLabel?: boolean }>`
  ${({ $hideLabel }) =>
    $hideLabel &&
    css`
      ${ACCESSIBLY_HIDDEN};
    `}
  color: ${colors.white};

  ${(props) =>
    props.status === RequirementStatus.Failed &&
    css`
      color: ${colors.orange};
    `}
`

const Requirements = styled.ul<RequirementsProps>`
  list-style: none;
  display: flex;
  flex-direction: column;

  position: absolute;
  bottom: 0;
  left: calc(100% + ${(props) => props.marginLeft}px);
  .mobile & {
    bottom: 40px;
    left: auto;
    right: -50px;
  }

  min-width: 150px;

  background: ${colors.blackPearl};
  color: ${colors.altoGray};
  text-align: left;

  font-weight: ${fonts.weight.medium};
  font-size: 9px;

  padding: 12px;
  border-radius: 8px;
  gap: 8px;
`

const SVG_PROPS = css`
  height: 10px;
  width: 10px;
  display: inline-block;
  margin-right: 7px;
`

const UncheckedIcon = styled.div`
  ${SVG_PROPS};

  border-radius: 50%;
  background: ${colors.transparent};
  border: 1px solid ${colors.altoGray};
`

const PassIcon = styled(SvgCheckCircleBroken)`
  ${SVG_PROPS};
  ${svgPathStyles(colors.neonGreen, 1.5)};
`

const FailedIcon = styled(SvgXCircle)`
  ${SVG_PROPS};
  ${svgPathStyles(colors.orange, 1.5)};
`

const RequirementItem = styled.li<RequirementProps>`
  display: flex;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 150ms;

  ${(props) => {
    switch (props.status) {
      case RequirementStatus.Unchecked:
        return css``
      case RequirementStatus.Failed:
        return css`
          color: ${colors.orange};
        `
      case RequirementStatus.Pass:
        return css`
          color: ${colors.neonGreen};
        `
    }
  }}
`

const Requirement: React.FC<RequirementProps> = (props) => {
  let icon
  switch (props.status) {
    case RequirementStatus.Unchecked:
      icon = <UncheckedIcon />
      break
    case RequirementStatus.Failed:
      icon = <FailedIcon />
      break
    case RequirementStatus.Pass:
      icon = <PassIcon />
      break
  }

  return (
    <RequirementItem status={props.status}>
      {icon}
      {props.children}
    </RequirementItem>
  )
}

/*
  INTERFACES
*/

enum RequirementType {
  MinLength = "MinLength",
  LowerCase = "LowerCase",
  UpperCase = "UpperCase",
  Numeral = "Numeral",
  SpecialChar = "SpecialChar",
  PasswordsMatch = "PasswordsMatch",
}

export enum RequirementStatus {
  Unchecked = "Unchecked",
  Failed = "Failed",
  Pass = "Pass",
}

interface Props {
  name: string
  placeholder: string
  autocomplete: "current-password" | "new-password"
  useNewInput?: boolean
  label?: string
  hideLabel?: boolean
  disabled?: boolean
  validate: (value: string, status: RequirementStatus) => void
  hideRequirements?: boolean
  validateMatch?: (value: string) => boolean
  autoFocus?: boolean
  className?: string
}

interface State {
  showRequirements: boolean
  readonly requirements: Map<RequirementType, RequirementStatus>
}

interface RequirementsProps {
  marginLeft: number
}

interface RequirementProps {
  status: RequirementStatus
  children?: React.ReactNode
}

/*
  COMPONENT
*/
export class Password extends React.Component<Props & RequirementsProps, State> {
  state: State = {
    showRequirements: false,
    requirements: new Map(),
  }

  render() {
    const status = this.status()
    const { hideLabel } = this.props
    const label = this.props.label && this.props.label.length > 0 ? this.props.label : "Password"
    return (
      <PasswordBlock className={this.props.className}>
        <PasswordLabel
          htmlFor={this.props.name}
          status={this.state.showRequirements && status}
          $hideLabel={hideLabel}
        >
          {label}
        </PasswordLabel>
        {this.props.useNewInput ? (
          <Input
            autoFocus={this.props.autoFocus}
            type="password"
            invalid={status === RequirementStatus.Failed}
            name={this.props.name}
            id={this.props.name}
            placeholder={this.props.placeholder}
            autoComplete={this.props.autocomplete}
            onChange={this.handleInputChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            disabled={this.props.disabled}
          />
        ) : (
          <DeprecatedInput
            autoFocus={this.props.autoFocus}
            type="password"
            name={this.props.name}
            id={this.props.name}
            placeholder={this.props.placeholder}
            autoComplete={this.props.autocomplete}
            onChange={this.handleInputChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            disabled={this.props.disabled}
          />
        )}
        {this.state.showRequirements && this.renderRequirements()}
      </PasswordBlock>
    )
  }

  renderRequirements() {
    const { validateMatch, marginLeft } = this.props
    return (
      <Requirements marginLeft={marginLeft}>
        {validateMatch && (
          <Requirement status={this.statusFor(RequirementType.PasswordsMatch)}>
            passwords match
          </Requirement>
        )}
        <Requirement status={this.statusFor(RequirementType.MinLength)}>
          a minimum of 10 characters
        </Requirement>
        <Requirement status={this.statusFor(RequirementType.LowerCase)}>
          a lower case letter
        </Requirement>
        <Requirement status={this.statusFor(RequirementType.UpperCase)}>
          an upper case letter
        </Requirement>
        <Requirement status={this.statusFor(RequirementType.Numeral)}>a number</Requirement>
        <Requirement status={this.statusFor(RequirementType.SpecialChar)}>
          a special character
        </Requirement>
      </Requirements>
    )
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value
    if (input.length === 0) {
      this.setState({ requirements: new Map() })

      // Failed because it is empty
      this.props.validate(input, RequirementStatus.Failed)
      return
    }

    this.resetRequirementStates(input)
  }

  handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target.value
    if (input.length === 0) {
      this.setState({ requirements: new Map() })
    } else {
      this.resetRequirementStates(input)
    }

    if (this.props.hideRequirements) {
      return
    }

    this.setState({ showRequirements: true })
  }

  handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ showRequirements: false })
    this.props.validate(event.target.value, this.status())
  }

  status() {
    const statuses = objectHelper
      .keysOf(RequirementType)
      .map((t) => this.statusFor(RequirementType[t]))

    if (statuses.every((status) => status === RequirementStatus.Pass)) {
      return RequirementStatus.Pass
    }

    if (statuses.some((status) => status === RequirementStatus.Failed)) {
      return RequirementStatus.Failed
    }

    return RequirementStatus.Unchecked
  }

  statusFor(type: RequirementType) {
    const { requirements } = this.state
    return requirements.get(type) || RequirementStatus.Unchecked
  }

  resetRequirementStates(input: string) {
    const requirements = objectHelper.keysOf(RequirementType).reduce((map, type) => {
      map.set(type as RequirementType, this.testInput(input, RequirementType[type]))
      return map
    }, new Map<RequirementType, RequirementStatus>())

    this.setState({ requirements }, () => {
      this.props.validate(input, this.status())
    })
  }

  testInput(input: string, type: RequirementType) {
    const { validateMatch } = this.props
    switch (type) {
      case RequirementType.PasswordsMatch:
        if (!validateMatch) return RequirementStatus.Pass
        return validateMatch(input) ? RequirementStatus.Pass : RequirementStatus.Failed

      case RequirementType.MinLength:
        return input.length >= MIN_LENGTH ? RequirementStatus.Pass : RequirementStatus.Failed

      case RequirementType.LowerCase:
        return LOWERCASE_REGEX.test(input) ? RequirementStatus.Pass : RequirementStatus.Failed

      case RequirementType.UpperCase:
        return UPPERCASE_REGEX.test(input) ? RequirementStatus.Pass : RequirementStatus.Failed

      case RequirementType.Numeral:
        return NUMERAL_REGEX.test(input) ? RequirementStatus.Pass : RequirementStatus.Failed

      case RequirementType.SpecialChar:
        return SPECIAL_REGEX.test(input) ? RequirementStatus.Pass : RequirementStatus.Failed
    }
  }
}
